"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/header/site-header";


// ─── Primitives (shared with Acuis design) ─────────────────────────────────────

interface AppScreenshotProps {
  src: string;
  alt?: string;
  className?: string;
  inverted?: boolean;
}

export function AppScreenshot({ src, alt = "", className = "", inverted = false }: AppScreenshotProps) {
  return (
    <div className={cn(
      "p-6 bg-secondary",
      inverted && "bg-background/10"
    )}>
      <div className={cn(
        "bg-background shadow-[0_0_20px_0_rgba(0,0,0,0.1)]",
      )}>
        <img src={src} alt={alt} className={cn(
          "w-full h-full py-3 object-cover",
          className
        )} />
      </div>
    </div>
  )
}

// ─── Header ───────────────────────────────────────────────────────────────────

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="max-w-6xl mx-auto pt-24 pb-24 px-6">
      <div className="max-w-4xl">
        <h1 className="font-serif text-3xl lg:text-6xl font-normal leading-[1.2] mb-12 text-foreground">
          Powerful, intuitive legal research tools for EU lawyers
        </h1>
        <p className="text-lg leading-relaxed text-foreground max-w-2xl mb-6 font-light">
          LawLibrary.AI is a cutting-edge platform for EU legal research that recommends legal authorities that speak to your legal questions or arguments, analyzes how they connect to each other, and annotates them with highlights, tags, and notes tailored to your task. It&apos;s like EUR-Lex, but better.
        </p>
        <Button size="lg" asChild className="bg-transparent border-2 border-foreground text-foreground rounded-none text-xs uppercase tracking-widest font-semibold hover:bg-foreground hover:text-background">
          <Link href="/sign-up">
            Get Started
          </Link>
        </Button>
      </div>
    </section>
  )
}

// ─── Why LawLibrary.AI ──────────────────────────────────────────────────────────

function WhyLawLibrary() {
  return (
    <div className="bg-secondary text-foreground">
      <div className="max-w-6xl mx-auto py-24 px-6">
        <div className="max-w-3xl">
          <h2 className="font-serif text-3xl md:text-5xl font-normal leading-snug mb-12">
            Why LawLibrary.AI?
          </h2>
          <p className="text-base leading-relaxed text-foreground mb-6 font-light">
            AI is going to revolutionize legal research. There&apos;s no getting around it. But we&apos;re taking a different approach than other companies.
          </p>
          <p className="text-base leading-relaxed text-foreground mb-6 font-light">
            Other companies offer AI legal assistants and claim they can accurately answer all of your legal questions. But even the best AI assistants often hallucinate — they can confidently provide inaccurate or incomplete answers. Don&apos;t risk it.
          </p>
          <p className="text-base leading-relaxed text-foreground font-light">
            An AI legal assistant is no replacement for your own judgment. LawLibrary.AI uses AI responsibly to create powerful, intuitive research tools that empower you to efficiently answer your legal questions and support your legal arguments.
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Legal research, simplified ─────────────────────────────────────────────────

function LegalResearchSimplified() {
  return (
    <section className="border-b border-border px-6">
      <div className="max-w-6xl mx-auto py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-normal leading-[1.2] mb-12 text-foreground">
            Legal research, simplified
          </h2>
          <p className="text-base leading-relaxed text-foreground mb-6 font-light">
            Have you ever done a keyword search on EUR-Lex and gotten literally hundreds or thousands of results? Not helpful. Where do you even start?
          </p>
          <p className="text-base leading-relaxed text-foreground mb-6 font-light">
            How are you supposed to figure out which court cases best answer your question or support your argument? How are you supposed to synthesize all those cases into a coherent answer or argument? How are you supposed to know which parts of a 400+ paragraph judgment have what you need?
          </p>
          <p className="text-base leading-relaxed text-foreground font-light">
            Legal research is complex and time-consuming, and correctness is paramount. But LawLibrary.AI provides powerful, end-to-end research tools that make legal research simpler, faster, and more accurate.
          </p>
        </div>
      </div>
    </section>
  )
}

// ─── How it works ───────────────────────────────────────────────────────────────

function HowItWorks() {
  const features = [
    {
      tagline: "AI-powered recommendations",
      heading: "Don't miss something important",
      paragraph: "Forget about keywords or search filters. Ask a question or summarize your legal argument and LawLibrary.AI will recommend the legal documents you need.",
      items: [
        "Don't mess around with clunky, frustrating search forms. Just ask a question in natural language.",
        "Leverage our extensive database — it's more complete than official sources, and it's updated daily.",
        "Don't know what law applies? Summarize your case facts and LawLibrary.AI will suggest relevant law.",
      ],
      image: "/app-recommend.svg",
    },
    {
      tagline: "AI-powered analysis",
      heading: "Make sense of complexity",
      paragraph: "Go beyond a list of search results. LawLibrary.AI visualizes how relevant documents are connected and estimates the precedential value of cases so you can see the bigger picture and decide which documents to explore further.",
      items: [
        "Explore an interactive citation map tailored to your task to see how the case law has developed.",
        "Ask AI to estimate the precedential value of cases to identify the most persuasive ones to cite.",
        "Ask AI to generate context-specific summaries of documents to help you prioritize what to read.",
      ],
      image: "/app-analyze.svg",
    },
    {
      tagline: "AI-powered annotations",
      heading: "Read efficiently and with purpose",
      paragraph: "Filter out the noise. Focus on what's important. Read legal documents with AI-powered, task-specific annotations using our clean, intuitive interface.",
      items: [
        "Ask AI to summarize a section or find paragraphs that answer your question or support your argument.",
        "Ask AI to create highlights, tags, and notes specific to your task so you can quickly find what you need.",
        "Download your custom-annotated document with a citation (PDF, TXT, CSV, XML, or HTML).",
      ],
      image: "/app-annotate.svg",
    },
  ];

  return (
    <div className="bg-secondary">
      <div className="max-w-6xl mx-auto py-24 px-6">
        <div className="max-w-3xl mb-14">
          <h2 className="font-serif text-[clamp(34px,4vw,56px)] font-normal leading-[1.08] tracking-[-0.025em] mb-6">
            How it works
          </h2>
          <p className="text-base leading-relaxed text-foreground mb-4 font-light">
            LawLibrary.AI streamlines the process of legal research. We recommend legal authorities that speak to your question or support your argument so you don&apos;t have to wade through hundreds of search results.
          </p>
          <p className="text-base leading-relaxed text-foreground mb-4 font-light">
            We analyze how relevant documents connect to each other so you can see the bigger picture, identify good law, and decide what to focus on.
          </p>
          <p className="text-base leading-relaxed text-foreground font-light">
            And we annotate these documents with highlights, tags, and notes specific to your question or argument so you can quickly find exactly what you need.
          </p>
        </div>
        <div className="flex flex-col gap-24">
          {features.map((f, i) => (
            <div
              key={f.tagline}
              className={cn(
                "grid grid-cols-1 md:grid-cols-2 gap-12 items-center",
              )}
            >
              <div className={cn(i % 2 === 1 && "md:order-2")}>
                <AppScreenshot src={f.image} alt={f.heading} inverted />
              </div>
              <div className={cn(i % 2 === 1 && "md:order-1")}>
                <p className="text-xs uppercase tracking-widest text-foreground font-semibold mb-3">{f.tagline}</p>
                <p className="text-2xl lg:text-4xl font-serif font-normal text-foreground mb-12">{f.heading}</p>
                <p className="text-sm text-foreground leading-relaxed mb-6">{f.paragraph}</p>
                <ul className="flex flex-col gap-3 list-disc pl-5">
                  {f.items.map((item, j) => (
                    <li key={j} className="text-sm text-foreground leading-relaxed font-light">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Collaborate with us ─────────────────────────────────────────────────────────

function Collaborate() {
  return (
    <section className="border-b border-border px-6 bg-[#2C67F6]">
      <div className="max-w-6xl mx-auto py-24">
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl md:text-5xl font-normal leading-[1.2] mb-6 text-white">
            Collaborate with us
          </h2>
          <p className="text-base leading-relaxed text-white/80 mb-12 font-light">
            LawLibrary.AI offers an industry-leading database of EU legal documents to legal tech companies via a subscription-based API. If you&apos;re providing legal research tools or developing a legal chatbot, and you want to support EU law, get in touch and we can talk about how our data can improve your product.
          </p>
          <Button size="lg" asChild className="bg-transparent border-2 border-white rounded-none text-xs uppercase tracking-widest font-semibold hover:bg-white hover:text-[#2C67F6]">
            <Link href="mailto:joshua.fjelstul@lawlibrary.ai">
              Contact us
            </Link>
          </Button>
        </div>
      </div>
    </section>
  )
}

// ─── Our purpose ─────────────────────────────────────────────────────────────────

function OurPurpose() {
  return (
    <section className="border-b border-border px-6">
      <div className="max-w-6xl mx-auto py-24">
        <div className="max-w-3xl">
          <h2 className="font-serif text-3xl md:text-5xl font-normal leading-[1.2] mb-12 text-foreground">
            Our purpose
          </h2>
          <p className="text-base leading-relaxed text-foreground mb-6 font-light">
            LawLibrary.AI was founded by researchers with extensive experience in studying EU law and courts. A key motivation for us in starting the company was to see our research generate impact beyond academic papers — to make life easier for people who work with EU law, both in research and practice.
          </p>
          <p className="text-base leading-relaxed text-foreground mb-6 font-light">
            We&apos;re inspired by the opportunity to increase access to justice by offering free subscriptions to our research platform for students and public interest NGOs.
          </p>
          <p className="text-base leading-relaxed text-foreground font-light">
            Our approach to the use of artificial intelligence (AI) and machine learning (ML) in legal research is guided by principles of transparency and accuracy. Our approach to data management centers on security and privacy protection.
          </p>
        </div>
      </div>
    </section>
  )
}

// ─── Our team ─────────────────────────────────────────────────────────────────────

function OurTeam() {
  const team = [
    {
      title: "Josh Fjelstul, CEO",
      icon: "/bio-photos/fjelstul.jpg",
      text: "Josh is a political economist, data scientist, and machine learning engineer who specializes in EU law and the politics of the CJEU. He has a BBA from the Canfield Business Honors Program (CBHP) at the McCombs School of Business at the University of Texas at Austin and a Ph.D. in Political Science from Emory University.",
    },
    {
      title: "Daniel Naurin, COO",
      icon: "/bio-photos/naurin.jpg",
      text: "Daniel is a political scientist who specializes in EU law and the politics of the CJEU. He's a Professor of Political Science and the Director of the ARENA Centre for European Studies at the University of Oslo. He's affiliated with the University of Gothenburg where he leads work on the IUROPA CJEU Database.",
    },
    {
      title: "Johan Lindholm, CPO",
      icon: "/bio-photos/lindholm.jpg",
      text: "Johan is a legal scholar who specializes in EU law, constitutional law, comparative law, sports law, and judicial politics. He's a Professor of Law at Umeå University and the Editor-in-Chief of the European Journal of Empirical Legal Studies.",
    },
  ];

  return (
    <section className="bg-secondary border-b border-border px-12">
      <div className="max-w-6xl mx-auto py-24">
        <div className="max-w-3xl mb-14">
          <h2 className="font-serif text-3xl md:text-5xl font-normal leading-[1.2] mb-6 text-foreground">
            Our team
          </h2>
          <p className="text-base leading-relaxed text-foreground font-light">
            LawLibrary.AI is developing innovative research tools that leverage artificial intelligence (AI) and machine learning (ML) to streamline the process of European Union (EU) legal research. Here&apos;s our team of co-founders.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-24">
          {team.map((member) => (
            <div key={member.title}>
              <img src={member.icon} alt={member.title} className="w-28 h-28 rounded-full object-cover mb-6 grayscale" />
              <p className="text-2xl font-serif font-medium text-foreground mb-3">{member.title}</p>
              <p className="text-sm text-foreground leading-relaxed font-light">{member.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── CTA ──────────────────────────────────────────────────────────────────────────

function CTA() {
  return (
    <section className="border-b border-border px-6 bg-[#111110]">
      <div className="max-w-6xl mx-auto px-8 py-32 text-center">
        <h2 className="font-serif text-3xl md:text-6xl font-normal leading-[1.3] mb-12 text-[#f5f5f2] max-w-4xl mx-auto">
          LawLibrary.AI
        </h2>
        <p className="text-sm text-background/75 font-light mb-10 max-w-2xl mx-auto leading-relaxed">
          LawLibrary.AI is a cutting-edge platform for EU legal research that recommends legal authorities that speak to your legal questions or arguments, analyzes how they connect to each other, and annotates them with highlights, tags, and notes tailored to your task. It&apos;s like EUR-Lex, but better.
        </p>
      </div>
    </section>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div className="w-full">
      <SiteHeader />
      <Hero />
      <WhyLawLibrary />
      <LegalResearchSimplified />
      <HowItWorks />
      <Collaborate />
      <OurPurpose />
      <OurTeam />
      <CTA />
    </div>
  )
}